import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FacebookReaction } from '@sb-tools/typescript/models/facebook-reaction.model';
import { FacebookUserReaction } from '../../../core/models/facebook-user-reaction.model';
import { TotalReactionsResponse } from '../../../core/models/total-reactions-response.model';
import { ReactionsDataService } from '../../../core/services/ticket/reactions-data.service';
import { TranslateModule } from '@ngx-translate/core';
import { ReactionIconComponent } from '../../../ticket-details/thread/ticket-thread-social-media/reaction-icon/reaction-icon.component';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { UserAvatarComponent } from '../../../../sb-modules/Frontend-User-Avatar-Module/user-avatar.component';
import { LoaderComponent } from '../../../../sb-tools/typescript/components/loader/loader.component';

@Component({
    selector: 'app-reactions-modal',
    templateUrl: './reactions-modal.component.html',
    styleUrls: ['./reactions-modal.component.scss'],
    imports: [
        TranslateModule,
        ReactionIconComponent,
        InlineSVGComponent,
        InfiniteScrollModule,
        UserAvatarComponent,
        LoaderComponent,
    ],
})
export class ReactionsModalComponent implements OnInit, OnDestroy {
    activeReaction: FacebookReaction = null;
    activeReactionData: FacebookUserReaction[] = [];
    isTicketReactions: boolean;
    isLoadingReactionData: boolean;
    isLoadingTotalCounts = true;
    replyId: number;
    socialMediaSourceId: number;
    ticketId: number;
    totalReactions: TotalReactionsResponse;

    private isReactionsLimitReached: boolean;
    private skipCount = 0;
    private takeCount = 10;

    private unsubscriber = new Subject<void>();

    constructor(
        public modalRef: BsModalRef,
        private reactionsDataService: ReactionsDataService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.loadTotalReactionsData();
    }

    loadMore(): void {
        if (this.isLoadingReactionData || this.isReactionsLimitReached) {
            return;
        }

        this.skipCount += this.takeCount;

        this.loadReactionData(this.activeReaction?.type);
    }

    openReactionInfo(reaction: FacebookReaction | null): void {
        if (this.activeReaction === reaction) {
            return;
        }

        this.activeReaction = reaction;

        this.skipCount = 0;
        this.isReactionsLimitReached = false;
        this.activeReactionData = [];

        if (!this.activeReaction) {
            this.activeReactionData = this.totalReactions.all;
        } else {
            this.loadReactionData(reaction.type);
        }
    }

    private loadReactionData(reactionType): void {
        this.isLoadingReactionData = true;

        if (this.isTicketReactions) {
            this.reactionsDataService
                .getPaginatedTicketReactions(
                    this.ticketId,
                    reactionType,
                    this.socialMediaSourceId,
                    this.takeCount,
                    this.skipCount,
                )
                .pipe(
                    finalize(() => {
                        this.isLoadingReactionData = false;
                    }),
                    takeUntil(this.unsubscriber),
                )
                .subscribe((reactions) => {
                    this.isReactionsLimitReached = reactions.length < this.takeCount;
                    this.activeReactionData = this.activeReactionData.concat(reactions);
                });
        } else {
            this.reactionsDataService
                .getPaginatedReplyReactions(
                    this.ticketId,
                    this.replyId,
                    reactionType,
                    this.socialMediaSourceId,
                    this.takeCount,
                    this.skipCount,
                )
                .pipe(
                    finalize(() => {
                        this.isLoadingReactionData = false;
                    }),
                    takeUntil(this.unsubscriber),
                )
                .subscribe((reactions) => {
                    this.isReactionsLimitReached = reactions.length < this.takeCount;
                    this.activeReactionData = this.activeReactionData.concat(reactions);
                });
        }

        this.changeDetectorRef.detectChanges();
    }

    private loadTotalReactionsData(): void {
        if (this.isTicketReactions) {
            this.reactionsDataService
                .getTicketReactions(this.ticketId, this.socialMediaSourceId)
                .pipe(
                    finalize(() => {
                        this.isLoadingTotalCounts = false;
                    }),
                    takeUntil(this.unsubscriber),
                )
                .subscribe((reactions) => {
                    this.totalReactions = reactions;
                    this.activeReactionData = reactions.all;
                });
        } else {
            this.reactionsDataService
                .getReplyReactions(this.ticketId, this.replyId, this.socialMediaSourceId)
                .pipe(
                    finalize(() => {
                        this.isLoadingTotalCounts = false;
                    }),
                    takeUntil(this.unsubscriber),
                )
                .subscribe((reactions) => {
                    this.totalReactions = reactions;
                    this.activeReactionData = reactions.all;
                });
        }
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
